import React, { useEffect, useState } from 'react';
import { api } from '../../config';
import {
    Box, Stepper, Step, StepLabel, Button, TextField, FormControl, FormLabel, Grid,
    Card, CardMedia, IconButton, Typography, Chip, Paper, CircularProgress, Stack,
    Backdrop, alpha, Container, Select, MenuItem, InputLabel, FormHelperText, Tooltip,
    Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import {
    Star as StarIcon,
    Delete as DeleteIcon,
    CloudUpload as UploadIcon,
    NavigateNext as NextIcon,
    NavigateBefore as BackIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';

const themeColors = {
    primary: '#99191b',
    secondary: '#e3764c',
    success: '#2e7d32',
    error: '#d32f2f',
    warning: '#ed6c02',
    info: '#0288d1'
};

// ====== CATEGORIE ======
const CATEGORIES = [
    { id: 2, nome: 'Sanità' },
    { id: 1, nome: 'Scolastico' },
    { id: 3, nome: 'PPP' }
];

const MAX_WORDS_DESC = 200;

const ProjectForm = ({
                         formData,
                         setFormData,
                         handleImagesChange,
                         removeImage,
                         setHeroImage,
                         handleSubmit,
                         loading,
                         onCancel,
                         projectId,
                         isEdit = false
                     }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState({
        titolo: '',
        citta: '',
        inizio: '',
        fine: '',
        categorie: '' // Cambiato da categoria_id a categorie
    });
    const [descExpanded, setDescExpanded] = useState(false);

    const steps = ['Informazioni Base', 'Dettagli e Immagini'];
    const yearRegex = /^\d{4}$/;

    // ========== NORMALIZZAZIONE ALL'INGRESSO ==========
    useEffect(() => {
        if (!formData) return;

        const next = { ...formData };

        // 1) is_important / isImportant -> coerenti (0/1 e boolean)
        const rawImportant = (next.is_important ?? next.isImportant ?? 0);
        const importantNum = typeof rawImportant === 'boolean'
            ? (rawImportant ? 1 : 0)
            : (Number(rawImportant) === 1 ? 1 : 0);
        next.is_important = importantNum;
        next.isImportant = importantNum === 1;

        // 2) Normalizza categorie: assicurati che sia sempre un array
        if (next.categorie == null) {
            next.categorie = [];
        } else if (typeof next.categorie === 'string') {
            // Se è una stringa (potrebbe essere da backend), converti in array
            try {
                next.categorie = JSON.parse(next.categorie);
            } catch {
                next.categorie = next.categorie.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            }
        } else if (!Array.isArray(next.categorie)) {
            next.categorie = [next.categorie].filter(Boolean);
        }

        // 3) Converti tutti gli ID categorie a numeri e rimuovi duplicati
        next.categorie = [...new Set(next.categorie.map(id => Number(id)).filter(id => !isNaN(id)))];

        // 4) Backward compatibility: se abbiamo categorie_nomi ma non categorie, mappa
        if ((next.categorie.length === 0) && next.categorie_nomi && Array.isArray(next.categorie_nomi)) {
            next.categorie = next.categorie_nomi.map(nome => {
                const cat = CATEGORIES.find(c => c.nome === nome);
                return cat ? cat.id : null;
            }).filter(id => id !== null);
        }

        // Aggiorna solo se ci sono cambiamenti reali
        const changed =
            next.is_important !== formData.is_important ||
            next.isImportant !== formData.isImportant ||
            JSON.stringify(next.categorie) !== JSON.stringify(formData.categorie);

        if (changed) {
            setFormData(next);
        }
    }, [formData, setFormData]);

    const getSubmitButtonText = () =>
        activeStep === steps.length - 1 ? (isEdit ? 'Aggiorna' : 'Salva') : 'Continua';
    const getSubmitButtonIcon = () =>
        activeStep === steps.length - 1 ? <SaveIcon /> : <NextIcon />;

    const getImageUrl = (image) => {
        if (image.preview) return image.preview;
        if (image.path && (image.path.startsWith('http') || image.path.startsWith('/'))) return image.path;
        if (image.path && projectId) return api(`/Progetti/${projectId}/${image.path}`);
        return image.path || '';
    };

    const wordCount = (str = '') => (str.trim() ? str.trim().split(/\s+/).length : 0);
    const descTooLong = wordCount(formData.descrizione) > MAX_WORDS_DESC;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'categorie') {
            // Gestione speciale per le categorie (multi-select)
            const selectedIds = typeof value === 'string'
                ? value.split(',').map(id => parseInt(id))
                : value;
            setFormData((prev) => ({ ...prev, [name]: selectedIds }));
            setErrors((p) => ({ ...p, categorie: selectedIds.length > 0 ? '' : 'Seleziona almeno una categoria' }));
        } else {
            const fieldValue = type === 'checkbox' ? checked : value;
            setFormData((prev) => ({ ...prev, [name]: fieldValue }));

            if (name === 'titolo') setErrors((p) => ({ ...p, titolo: fieldValue.trim() ? '' : 'Il titolo è obbligatorio' }));
            if (name === 'citta') setErrors((p) => ({ ...p, citta: fieldValue.trim() ? '' : 'La città è obbligatoria' }));
            if (name === 'inizio') setErrors((p) => ({ ...p, inizio: fieldValue && yearRegex.test(fieldValue) ? '' : 'Anno a 4 cifre (es. 2023)' }));
            if (name === 'fine') setErrors((p) => ({ ...p, fine: fieldValue && yearRegex.test(fieldValue) ? '' : 'Anno a 4 cifre (es. 2024)' }));
        }
    };

    const toggleImportant = () => {
        setFormData((prev) => {
            const starOn = (
                typeof prev.isImportant === 'boolean'
                    ? prev.isImportant
                    : Number(prev.is_important) === 1
            );
            const next = !starOn;
            return {
                ...prev,
                isImportant: next,          // per UI (boolean)
                is_important: next ? 1 : 0  // per backend (0/1)
            };
        });
    };

    const validateStep0 = () => {
        const e = {
            titolo: formData.titolo?.trim() ? '' : 'Il titolo è obbligatorio',
            citta: formData.citta?.trim() ? '' : 'La città è obbligatoria',
            categorie: (formData.categorie && formData.categorie.length > 0) ? '' : 'Seleziona almeno una categoria'
        };
        setErrors((prev) => ({ ...prev, ...e }));
        return !e.titolo && !e.citta && !e.categorie;
    };

    const validateStep1 = () => {
        const e = {
            inizio: formData.inizio && yearRegex.test(formData.inizio) ? '' : 'Anno a 4 cifre (es. 2023)',
            fine: formData.fine && yearRegex.test(formData.fine) ? '' : 'Anno a 4 cifre (es. 2024)'
        };
        setErrors((prev) => ({ ...prev, ...e }));
        return !e.inizio && !e.fine;
    };

    const handleNext = () => {
        if (activeStep === 0 && !validateStep0()) return;
        if (activeStep === 1 && !validateStep1()) return;
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

    const onPrimaryClick = (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }

        if (activeStep < steps.length - 1) {
            handleNext();
            return;
        }

        if (!validateStep0() || !validateStep1()) return;

        // normalizza payload prima del submit
        const normalized = {
            ...formData,
            is_important: typeof formData.isImportant !== 'undefined' ? (formData.isImportant ? 1 : 0)
                : (formData.is_important ? 1 : 0),
            categorie: formData.categorie || []
        };

        // Passa i dati normalizzati
        handleSubmit(normalized);
    };

    const renderStep1 = () => {
        // derivazione robusta dello stato della stella
        const starOn = (
            typeof formData.isImportant === 'boolean'
                ? formData.isImportant
                : Number(formData.is_important) === 1
        );

        return (
            <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                    label="Titolo"
                    name="titolo"
                    value={formData.titolo || ''}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={!!errors.titolo}
                    helperText={errors.titolo}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: themeColors.primary },
                            '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                        }
                    }}
                />

                <TextField
                    label="Sottotitolo"
                    name="sottotitolo"
                    value={formData.sottotitolo || ''}
                    onChange={handleChange}
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: themeColors.primary },
                            '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                        }
                    }}
                />

                <TextField
                    label="Città"
                    name="citta"
                    value={formData.citta || ''}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={!!errors.citta}
                    helperText={errors.citta}
                    FormHelperTextProps={{ sx: { textAlign: 'right', ml: 0, mr: 0 } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: themeColors.primary },
                            '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                        }
                    }}
                />

                {/* Multi-select per categorie + stella evidenza */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <FormControl
                        fullWidth
                        size="small"
                        disabled={loading}
                        error={!!errors.categorie}
                        sx={{ flex: 1 }}
                    >
                        <InputLabel id="categorie-label">Categorie</InputLabel>
                        <Select
                            labelId="categorie-label"
                            label="Categorie"
                            name="categorie"
                            multiple
                            value={formData.categorie || []}
                            onChange={handleChange}
                            input={<OutlinedInput label="Categorie" />}
                            renderValue={(selected) => {
                                const selectedNames = selected.map(id => {
                                    const cat = CATEGORIES.find(c => c.id === id);
                                    return cat ? cat.nome : '';
                                }).filter(name => name !== '');
                                return selectedNames.join(', ');
                            }}
                            sx={{
                                borderRadius: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary }
                            }}
                        >
                            {CATEGORIES.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                    <Checkbox checked={(formData.categorie || []).indexOf(c.id) > -1} />
                                    <ListItemText primary={c.nome} />
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {errors.categorie || `Selezionate: ${(formData.categorie || []).length} categoria(e)`}
                        </FormHelperText>
                    </FormControl>

                    {/* Bottone stella evidenza */}
                    <Tooltip title={starOn ? 'In evidenza' : 'Segna come evidenziato'}>
                        <span>
                            <IconButton
                                aria-pressed={starOn}
                                onClick={toggleImportant}
                                disabled={loading}
                                sx={{
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: starOn ? 'transparent' : 'divider',
                                    backgroundColor: starOn ? '#ffeb3b' : 'transparent',
                                    color: starOn ? '#7a5e00' : 'text.secondary',
                                    width: 40,
                                    height: 40,
                                    mt: 0.5,
                                    '&:hover': { backgroundColor: starOn ? '#ffd54f' : 'grey.100' },
                                    boxShadow: starOn ? `0 2px 8px ${alpha('#ffeb3b', 0.5)}` : 'none'
                                }}
                            >
                                <StarIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {/* Descrizione */}
                <Box sx={{ position: 'relative' }}>
                    <TextField
                        label="Descrizione"
                        name="descrizione"
                        value={formData.descrizione || ''}
                        onChange={(e) => {
                            handleChange(e);
                            if (wordCount(e.target.value) <= MAX_WORDS_DESC) setDescExpanded(false);
                        }}
                        disabled={loading}
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        minRows={6}
                        placeholder="Inserisci una descrizione del progetto..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                alignItems: 'flex-start',
                                '& textarea': {
                                    lineHeight: 1.5,
                                    transition: 'max-height 0.2s ease',
                                    maxHeight: descTooLong && !descExpanded ? 140 : 'none',
                                    overflow: descTooLong && !descExpanded ? 'hidden' : 'auto'
                                },
                                '&:hover fieldset': { borderColor: themeColors.primary },
                                '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                            }
                        }}
                    />
                    {descTooLong && !descExpanded && (
                        <Box
                            aria-hidden
                            sx={{
                                pointerEvents: 'none',
                                position: 'absolute',
                                left: 16,
                                right: 16,
                                bottom: 42,
                                height: 40,
                                background: `linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.9) 40%, rgba(255,255,255,1) 100%)`,
                                borderBottomLeftRadius: 8,
                                borderBottomRightRadius: 8
                            }}
                        />
                    )}
                    {descTooLong && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Button
                                onClick={() => setDescExpanded((v) => !v)}
                                size="small"
                                sx={{
                                    borderRadius: 2,
                                    px: 2,
                                    fontWeight: 600,
                                    color: themeColors.primary,
                                    '&:hover': { backgroundColor: alpha(themeColors.primary, 0.08) }
                                }}
                            >
                                {descExpanded ? 'Mostra di meno' : 'Mostra di più'}
                            </Button>
                        </Box>
                    )}
                </Box>

                <TextField
                    label="Committente"
                    name="committente"
                    value={formData.committente || ''}
                    onChange={handleChange}
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 255 }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: themeColors.primary },
                            '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                        }
                    }}
                />
            </Stack>
        );
    };

    const renderStep2 = () => (
        <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
                label="Incarico"
                name="incarico"
                value={formData.incarico || ''}
                onChange={handleChange}
                required
                disabled={loading}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': { borderColor: themeColors.primary },
                        '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                    }
                }}
            />

            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <TextField
                        label="Anno Inizio"
                        name="inizio"
                        value={formData.inizio || ''}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        fullWidth
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 4, pattern: '\\d{4}' }}
                        helperText={errors.inizio || '4 cifre'}
                        error={!!errors.inizio}
                        FormHelperTextProps={{ sx: { textAlign: 'right', ml: 0, mr: 0 } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': { borderColor: themeColors.primary },
                                '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Anno Fine"
                        name="fine"
                        value={formData.fine || ''}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        fullWidth
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 4, pattern: '\\d{4}' }}
                        helperText={errors.fine || '4 cifre'}
                        error={!!errors.fine}
                        FormHelperTextProps={{ sx: { textAlign: 'right', ml: 0, mr: 0 } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': { borderColor: themeColors.primary },
                                '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                            }
                        }}
                    />
                </Grid>
            </Grid>

            <FormControl fullWidth>
                <FormLabel component="legend" sx={{ mb: 1, color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
                    Carica Immagini
                </FormLabel>
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={loading}
                    fullWidth
                    size="small"
                    sx={{
                        py: 1.5,
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        backgroundColor: 'grey.50',
                        '&:hover': { backgroundColor: 'grey.100', borderColor: themeColors.primary }
                    }}
                >
                    Seleziona Immagini
                    <input type="file" hidden multiple accept="image/*" onChange={handleImagesChange} />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'right', display: 'block' }}>
                    Formati supportati: JPG, PNG, GIF. Dimensione massima: 5MB per immagine
                </Typography>
            </FormControl>

            {formData.images && formData.images.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: themeColors.primary, mt: 1, textAlign: 'right' }}>
                        Immagini Caricate ({formData.images.length})
                    </Typography>

                    <Box
                        sx={{
                            height: '400px',
                            overflow: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 1,
                            backgroundColor: 'grey.50'
                        }}
                    >
                        <Grid container spacing={1}>
                            {formData.images.map((image, index) => {
                                const imageUrl = getImageUrl(image);
                                return (
                                    <Grid item xs={6} key={image.id || index}>
                                        <Card
                                            sx={{
                                                position: 'relative',
                                                border: image.isHero ? 2 : 1,
                                                borderColor: image.isHero ? themeColors.primary : 'divider',
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                boxShadow: image.isHero ? `0 2px 12px ${alpha(themeColors.primary, 0.2)}` : 1,
                                                transition: 'all 0.2s ease',
                                                height: '140px',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 4px 15px ${alpha(themeColors.primary, 0.15)}`
                                                }
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                height="100"
                                                src={imageUrl}
                                                alt={image.alt || `Anteprima ${index + 1}`}
                                                onError={(e) => {
                                                    console.error('Errore nel caricamento immagine:', imageUrl);
                                                    e.target.style.display = 'none';
                                                    const card = e.target.closest('.MuiCard-root');
                                                    if (card) {
                                                        const placeholder = card.querySelector('.image-placeholder');
                                                        if (placeholder) placeholder.style.display = 'flex';
                                                    }
                                                }}
                                                onLoad={(e) => {
                                                    const card = e.target.closest('.MuiCard-root');
                                                    if (card) {
                                                        const placeholder = card.querySelector('.image-placeholder');
                                                        if (placeholder) placeholder.style.display = 'none';
                                                    }
                                                }}
                                                sx={{ objectFit: 'cover', width: '100%', height: '100px' }}
                                            />

                                            <Box
                                                className="image-placeholder"
                                                sx={{
                                                    display: 'none',
                                                    height: '100px',
                                                    backgroundColor: 'grey.100',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexDirection: 'column',
                                                    color: 'grey.500'
                                                }}
                                            >
                                                <UploadIcon />
                                                <Typography variant="caption" sx={{ mt: 1 }}>
                                                    Immagine non disponibile
                                                </Typography>
                                            </Box>

                                            {image.isHero && (
                                                <Chip
                                                    label="COPERTINA"
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        left: 4,
                                                        backgroundColor: themeColors.primary,
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        borderRadius: 1,
                                                        height: 20,
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            )}

                                            <Box
                                                sx={{
                                                    p: 0.5,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    borderTop: '1px solid',
                                                    borderColor: 'divider',
                                                    gap: 0.5
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setHeroImage(index)}
                                                    disabled={image.isHero || loading}
                                                    sx={{
                                                        backgroundColor: image.isHero ? themeColors.primary : 'transparent',
                                                        color: image.isHero ? 'white' : themeColors.primary,
                                                        borderRadius: 1,
                                                        width: 28,
                                                        height: 28,
                                                        flex: 1,
                                                        '&:hover': {
                                                            backgroundColor: image.isHero ? themeColors.primary : themeColors.secondary,
                                                            color: 'white'
                                                        }
                                                    }}
                                                    title={image.isHero ? 'Immagine copertina' : 'Imposta come copertina'}
                                                >
                                                    <StarIcon fontSize="small" />
                                                </IconButton>

                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeImage(index)}
                                                    disabled={loading}
                                                    sx={{
                                                        backgroundColor: 'transparent',
                                                        color: themeColors.error,
                                                        borderRadius: 1,
                                                        width: 28,
                                                        height: 28,
                                                        flex: 1,
                                                        '&:hover': { backgroundColor: themeColors.error, color: 'white' }
                                                    }}
                                                    title="Rimuovi immagine"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                </Box>
            )}
        </Stack>
    );

    return (
        <Container maxWidth="sm" sx={{ py: 2 }}>
            <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: alpha(themeColors.primary, 0.03) }}>
                    <Stepper
                        activeStep={activeStep}
                        orientation="horizontal"
                        sx={{
                            '& .MuiStepLabel-root .Mui-completed': { color: themeColors.primary },
                            '& .MuiStepLabel-root .Mui-active': { color: themeColors.primary },
                            '& .MuiStepLabel-label': {
                                fontWeight: 600, fontSize: '0.9rem',
                                '&.Mui-active': { color: themeColors.primary },
                                '&.Mui-completed': { color: themeColors.primary }
                            },
                            '& .MuiStepConnector-line': { borderColor: themeColors.primary, borderTopWidth: 2, borderRadius: 1 }
                        }}
                    >
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel
                                    sx={{
                                        '& .MuiStepIcon-root': {
                                            color: 'grey.300',
                                            '&.Mui-completed': { color: themeColors.primary },
                                            '&.Mui-active': { color: themeColors.primary },
                                            width: 28,
                                            height: 28,
                                            '& .MuiStepIcon-text': { fontSize: '0.8rem' }
                                        }
                                    }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <Box sx={{ p: 3 }}>
                    {activeStep === 0 && renderStep1()}
                    {activeStep === 1 && renderStep2()}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 3,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Box>
                            {activeStep > 0 && (
                                <Button
                                    disabled={loading}
                                    onClick={handleBack}
                                    startIcon={<BackIcon />}
                                    size="small"
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        border: `1px solid ${themeColors.primary}`,
                                        color: themeColors.primary,
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            backgroundColor: alpha(themeColors.primary, 0.1),
                                            transform: 'translateY(-1px)'
                                        }
                                    }}
                                >
                                    Indietro
                                </Button>
                            )}
                        </Box>

                        <Stack direction="row" spacing={1}>
                            {activeStep === 1 && (
                                <Button
                                    onClick={onCancel}
                                    disabled={loading}
                                    startIcon={<CancelIcon />}
                                    size="small"
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        '&:hover': { backgroundColor: 'grey.100', transform: 'translateY(-1px)' }
                                    }}
                                >
                                    Annulla
                                </Button>
                            )}

                            <Button
                                variant="contained"
                                onClick={onPrimaryClick}
                                disabled={loading}
                                startIcon={getSubmitButtonIcon()}
                                size="small"
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    background: `linear-gradient(90deg, ${themeColors.primary} 0%, ${themeColors.secondary} 100%)`,
                                    '&:hover': {
                                        background: `linear-gradient(90deg, ${themeColors.primary} 30%, ${themeColors.secondary} 100%)`,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(themeColors.primary, 0.3)}`
                                    },
                                    fontWeight: 600,
                                    fontSize: '0.875rem'
                                }}
                            >
                                {getSubmitButtonText()}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Paper>

            {loading && (
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: alpha('#000', 0.8) }}
                    open={loading}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            p: 3,
                            backgroundColor: 'white',
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                        }}
                    >
                        <CircularProgress size={40} sx={{ color: themeColors.primary }} />
                        <Typography variant="subtitle1" sx={{ color: themeColors.primary, fontWeight: 600 }}>
                            {isEdit ? 'Aggiornamento in corso...' : 'Salvataggio in corso...'}
                        </Typography>
                    </Box>
                </Backdrop>
            )}
        </Container>
    );
};

export default ProjectForm;